/**
 * SubcategoryPicker — composes LookupPicker for provider_subcategories
 * (Block 6.B.2).
 */

import { useCallback, type ReactNode } from 'react';
import { useTenant } from '@/lib/auth';
import { canAddLookup, lookupsApi } from '@/lib/lookups';
import type { LookupRow } from '@/lib/types';
import { LookupPicker } from './LookupPicker';

export interface SubcategoryPickerProps {
  providerId: string;
  /** Currently-saved subcategorizations on this provider. */
  selected: LookupRow[];
  /** Re-fetch the parent ProviderDetail after a successful add/remove. */
  onMutated: () => Promise<void>;
  /**
   * Phase 1.11 — read-only render when false. Defaults to true. Consumer
   * gates on `canDirectWrite(whoami)` at the call site.
   */
  canEdit?: boolean;
}

export function SubcategoryPicker({
  providerId,
  selected,
  onMutated,
  canEdit = true,
}: SubcategoryPickerProps): ReactNode {
  const { tenantSlug, whoami } = useTenant();

  const loadOptions = useCallback(
    async (q: string, limit: number): Promise<LookupRow[]> => {
      const r = await lookupsApi.listSubcategories(tenantSlug, { q, limit });
      return r.rows;
    },
    [tenantSlug],
  );

  const onAdd = useCallback(
    async (slug: string): Promise<void> => {
      await lookupsApi.addProviderSubcategorization(tenantSlug, providerId, { slug });
      await onMutated();
    },
    [tenantSlug, providerId, onMutated],
  );

  const onRemove = useCallback(
    async (subcategoryId: string): Promise<void> => {
      await lookupsApi.removeProviderSubcategorization(tenantSlug, providerId, subcategoryId);
      await onMutated();
    },
    [tenantSlug, providerId, onMutated],
  );

  const onCreate = useCallback(
    async (displayName: string): Promise<{ slug: string; display_name: string }> => {
      const created = await lookupsApi.addSubcategory(tenantSlug, { display_name: displayName });
      return { slug: created.slug, display_name: created.display_name };
    },
    [tenantSlug],
  );

  return (
    <LookupPicker<LookupRow>
      selected={selected}
      getLabel={(item) => item.display_name}
      getId={(item) => item.id}
      getSlug={(item) => item.slug}
      loadOptions={loadOptions}
      onAdd={onAdd}
      onRemove={onRemove}
      seeding={{
        canAdd: canAddLookup(whoami),
        onCreate,
        typeLabel: 'subcategory',
      }}
      label="subcategory"
      placeholder="Search service types"
      chipVariant="service"
      loadOptionsLimit={200}
      canEdit={canEdit}
    />
  );
}
